import { useEffect, useState, useCallback } from "react";
import { FaDownload } from "react-icons/fa";
import api from "../../../../../utils/axiosInstance";
import { btn } from "../../../../../components/Button/buttonStyles";
import { toast, confirmDialog } from "../../../../../components/Toast/toast";
import UploadQuotationFileModal from "./UploadQuotationFileModal";
import { formatDateTime } from "../../quotationFormat";

const HEADERS = ["File #", "Filename", "Category", "Note", "Size", "Uploaded at", "Uploaded by", ""];
const CELL = "px-3 py-2 text-xs whitespace-nowrap text-gray-700";

const formatSize = (bytes) => {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(2)} KB` : `${(kb / 1024).toFixed(2)} MB`;
};

const uploaderName = (file) => {
  const details = file.uploader?.details;
  if (details?.first_name) return `${details.first_name} ${details.last_name || ""}`.trim();
  return file.uploader?.email || "—";
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

const FilesTab = ({ quotationId, quotation, locked }) => {
  const [files, setFiles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/quotations/${quotationId}/files`);
      setFiles(res.data.data || []);
      setSelectedIds([]);
    } catch (err) {
      console.error("Error fetching quotation files:", err);
      toast.error("Failed to fetch quotation files");
    } finally {
      setLoading(false);
    }
  }, [quotationId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const allSelected = files.length > 0 && selectedIds.length === files.length;

  const toggleOne = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const toggleAll = () => setSelectedIds(allSelected ? [] : files.map((f) => f.id));

  const handleDownload = async (file) => {
    try {
      const res = await api.get(`/download-file?path=${encodeURIComponent(file.file_path)}`, {
        responseType: "blob",
      });
      saveBlob(res.data, file.original_file_name || "file");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download file");
    }
  };

  const handleDelete = async () => {
    const count = selectedIds.length;
    if (!(await confirmDialog(`Delete ${count === 1 ? "this file" : `these ${count} files`}?`))) return;

    setBusy(true);
    try {
      const res = await api.post(`/quotations/${quotationId}/files/delete`, { file_ids: selectedIds });
      toast.success(res.data.message);
      fetchFiles();
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error(err.response?.data?.message || "Failed to delete files");
    } finally {
      setBusy(false);
    }
  };

  // Downloads the ticked files, or all of them when nothing is ticked.
  const handleDownloadZip = async () => {
    if (!files.length) return toast.error("No files to download");

    setBusy(true);
    try {
      const query = selectedIds.length ? `?ids=${selectedIds.join(",")}` : "";
      const res = await api.get(`/quotations/${quotationId}/files/download-zip${query}`, {
        responseType: "blob",
      });
      saveBlob(res.data, `${quotation?.quotation_code || `quotation-${quotationId}`}-files.zip`);
    } catch (err) {
      console.error("Zip download failed:", err);
      toast.error("Failed to download the files");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="flex justify-between items-center mb-3 flex-wrap gap-3">
        <h2 className="font-bold text-gray-900">
          Files <span className="text-gray-500 font-medium">({files.length})</span>
        </h2>

        <div className="flex items-center gap-2">
          {!locked && (
            <>
              <button
                onClick={handleDelete}
                disabled={selectedIds.length === 0 || busy}
                className={btn("danger", "sm")}
              >
                Delete{selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
              </button>
              <span className="text-gray-300">|</span>
              <button onClick={() => setShowUpload(true)} className={btn("primary", "sm")}>
                Upload
              </button>
              <button
                onClick={() => toast.info("Add URLs is coming soon.")}
                className={btn("primary", "sm")}
              >
                Add URLs
              </button>
            </>
          )}
          <button onClick={handleDownloadZip} disabled={busy} className={btn("primary", "sm")}>
            Download as ZIP
          </button>
        </div>
      </div>

      <div className="w-full bg-white shadow rounded-lg overflow-hidden">
        <div className="max-h-[500px] overflow-y-auto overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
            </div>
          ) : (
            <table className="w-full table-auto border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                  {!locked && (
                    <th className="px-3 py-2 w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = selectedIds.length > 0 && !allSelected;
                        }}
                        onChange={toggleAll}
                        disabled={files.length === 0}
                        className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                        aria-label="Select all files"
                      />
                    </th>
                  )}
                  {HEADERS.map((h, i) => (
                    <th key={h || `actions-${i}`} className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {files.length === 0 ? (
                  <tr>
                    <td
                      colSpan={HEADERS.length + (locked ? 0 : 1)}
                      className="py-10 text-center text-gray-400 text-xs"
                    >
                      No results found.
                    </td>
                  </tr>
                ) : (
                  files.map((file, index) => {
                    const selected = selectedIds.includes(file.id);
                    return (
                      <tr
                        key={file.id}
                        className={`${
                          selected ? "bg-brand-50" : index % 2 === 0 ? "bg-gray-50" : "bg-white"
                        } hover:bg-brand-50 transition-colors`}
                      >
                        {!locked && (
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleOne(file.id)}
                              className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                              aria-label={`Select ${file.original_file_name}`}
                            />
                          </td>
                        )}
                        <td className={CELL}>{file.file_code}</td>
                        <td className="px-3 py-2 text-xs break-words">
                          <button
                            onClick={() => handleDownload(file)}
                            className="text-brand-600 hover:text-brand-800 underline font-medium"
                          >
                            {file.original_file_name}
                          </button>
                        </td>
                        <td className={CELL}>{file.category || "—"}</td>
                        <td className="px-3 py-2 text-xs text-gray-700 break-words">{file.note || "—"}</td>
                        <td className={CELL}>{formatSize(file.file_size)}</td>
                        <td className={CELL}>{formatDateTime(file.uploaded_at)}</td>
                        <td className={CELL}>{uploaderName(file)}</td>
                        <td className={CELL}>
                          <button
                            onClick={() => handleDownload(file)}
                            className="text-gray-400 hover:text-brand-600 transition-colors"
                            title="Download"
                          >
                            <FaDownload />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showUpload && (
        <UploadQuotationFileModal
          quotationId={quotationId}
          onClose={() => setShowUpload(false)}
          onSuccess={() => {
            setShowUpload(false);
            fetchFiles();
          }}
        />
      )}
    </section>
  );
};

export default FilesTab;
